/**
 * CPD — Module Player Page (/modules/:id)
 *
 * Stacked layout: full-width video at top, Study Guide | Quiz tabs below.
 * Quiz submit calls POST /api/modules/:id/complete and renders the
 * Certificate card inline on pass.
 */

import { useEffect, useState } from 'react';
import { Link, useRoute } from 'wouter';
import Markdown from 'react-markdown';
import { AppLayout } from '@/layouts/AppLayout';
import { CertCard } from '@/components/features/CertCard';
import { api, ApiError } from '@/lib/api';
import type { ModuleCompleteResult, ModuleDetail } from '@/lib/types';

type Tab = 'guide' | 'quiz';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'guide', label: 'Study Guide', icon: 'menu_book' },
  { id: 'quiz',  label: 'Quiz',        icon: 'fact_check' },
];

function ModuleContent({ id }: { id: string }): React.ReactElement {
  const [mod, setMod] = useState<ModuleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('guide');
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ModuleCompleteResult | null>(null);
  const [existingCert, setExistingCert] = useState<import('@/lib/types').ModuleCertificate | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.get<ModuleDetail>(`/api/modules/${id}`);
        if (cancelled) return;
        setMod(data);
        // If already completed, load the existing certificate
        if (data.completed && data.certId) {
          try {
            const cert = await api.get<import('@/lib/types').ModuleCertificate>(`/api/modules/${id}/cert`);
            if (!cancelled) setExistingCert(cert);
          } catch { /* cert fetch failure is non-fatal */ }
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : 'Failed to load module.');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const submitQuiz = async (): Promise<void> => {
    if (!mod) return;
    setSubmitting(true);
    try {
      const data = await api.post<ModuleCompleteResult>(`/api/modules/${mod.id}/complete`, { answers });
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit quiz.');
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div role="alert" className="rounded-lg bg-[var(--color-error-100,#fee2e2)] px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!mod) {
    return (
      <div className="flex items-center justify-center py-24">
        <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary)] text-4xl">progress_activity</span>
      </div>
    );
  }

  return (
    <div className="stitch-font-body text-[var(--stitch-on-surface)] max-w-4xl">
      <div className="mb-4">
        <Link href="/learn" className="stitch-font-label text-[13px] text-[var(--stitch-primary)] no-underline inline-flex items-center gap-1">
          <span className="material-symbols-outlined text-[18px]">arrow_back</span> Learn Library
        </Link>
      </div>

      <header className="mb-6">
        <span className="stitch-font-label text-[12px] text-[var(--stitch-surface-tint)] tracking-[0.2em] uppercase">
          {mod.category} · {mod.hours} hr
        </span>
        <h1 className="stitch-font-display text-[30px] md:text-[40px] text-[var(--stitch-primary)] uppercase tracking-tight leading-tight mt-1">
          {mod.title}
        </h1>
        <p className="stitch-font-body text-[15px] text-[var(--stitch-on-surface-variant)] mt-2 max-w-2xl">{mod.description}</p>
      </header>

      {/* Video */}
      {mod.videoUrl && (
        <div className="aspect-video w-full rounded-xl overflow-hidden stitch-editorial-shadow bg-black mb-8">
          <iframe
            src={mod.videoUrl}
            title={`${mod.title} — video`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      )}

      {/* Podcast / audio */}
      {mod.podcastUrl && (
        <div className="rounded-xl border border-[var(--stitch-outline-variant)] bg-[var(--stitch-surface-container-lowest)] p-5 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="material-symbols-outlined text-[var(--stitch-primary)]">headphones</span>
            <span className="stitch-font-label text-[12px] text-[var(--stitch-primary)] tracking-[0.15em] uppercase">
              Podcast / Audio
            </span>
          </div>
          <audio
            controls
            src={mod.podcastUrl}
            className="w-full"
            aria-label={`${mod.title} — audio`}
          >
            <a href={mod.podcastUrl} target="_blank" rel="noopener noreferrer">
              Download audio
            </a>
          </audio>
        </div>
      )}

      {/* Existing certificate — shown when navigating directly to a completed module */}
      {existingCert && !result && (
        <div className="mb-8">
          <CertCard cert={existingCert} />
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" aria-label="Module sections" className="flex gap-2 overflow-x-auto border-b border-[var(--stitch-outline-variant)] mb-8">
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => { setTab(t.id); }}
              className={`flex items-center gap-2 px-5 py-3 stitch-font-label text-[13px] uppercase whitespace-nowrap border-b-2 -mb-px transition-colors ${
                active
                  ? 'text-[var(--stitch-primary)] border-[var(--stitch-primary)]'
                  : 'text-[var(--stitch-on-surface-variant)] border-transparent hover:text-[var(--stitch-primary)]'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">{t.icon}</span> {t.label}
            </button>
          );
        })}
      </div>

      {/* Study guide */}
      {tab === 'guide' && (
        <div className="prose prose-slate max-w-none bg-[var(--stitch-surface-container-lowest)] p-8 rounded-xl stitch-editorial-shadow stitch-font-body text-[15px] leading-relaxed text-[var(--stitch-on-surface)]">
          <Markdown>{mod.studyGuideMarkdown}</Markdown>
        </div>
      )}

      {/* Quiz */}
      {tab === 'quiz' && (
        <div className="bg-[var(--stitch-surface-container-lowest)] p-8 rounded-xl stitch-editorial-shadow">
          {!result && (
            <>
              <h2 className="stitch-font-headline text-[22px] uppercase text-[var(--stitch-primary)] mb-6">Quiz</h2>
              <ol className="space-y-8">
                {mod.quiz.map((q, qIndex) => (
                  <li key={q.id}>
                    <p className="stitch-font-body text-[16px] font-medium text-[var(--stitch-on-surface)] mb-3">
                      {qIndex + 1}. {q.question}
                    </p>
                    <div className="grid gap-2">
                      {q.options.map((opt, optIndex) => {
                        const selected = answers[q.id] === optIndex;
                        return (
                          <label
                            key={optIndex}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                              selected
                                ? 'border-[var(--stitch-primary)] bg-[var(--stitch-primary-fixed)]/30'
                                : 'border-[var(--stitch-outline-variant)] hover:bg-[var(--stitch-surface-container-low)]'
                            }`}
                          >
                            <input
                              type="radio"
                              name={q.id}
                              value={optIndex}
                              checked={selected}
                              onChange={() => { setAnswers(a => ({ ...a, [q.id]: optIndex })); }}
                              className="accent-[var(--stitch-primary)]"
                            />
                            <span className="stitch-font-body text-[14px]">{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-8 flex justify-end">
                <button
                  type="button"
                  onClick={() => { void submitQuiz(); }}
                  disabled={submitting || Object.keys(answers).length < mod.quiz.length}
                  className="px-8 py-4 bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] rounded-full stitch-font-label text-[13px] uppercase tracking-widest font-bold hover:bg-[var(--stitch-primary-container)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-8">
              <div>
                <h2 className="stitch-font-headline text-[24px] uppercase text-[var(--stitch-primary)] mb-2">
                  {result.passed ? 'Module complete.' : 'Not quite — review and retry.'}
                </h2>
                <p className="stitch-font-body text-[15px] text-[var(--stitch-on-surface-variant)]">
                  You scored {result.score} of {result.total}
                  {result.passed ? '. Your certificate is ready below.' : '.'}
                </p>
              </div>

              {result.certificate && <CertCard cert={result.certificate} />}

              {/* Auto-created activity + reflection notice */}
              {result.autoActivity && (
                <div className="rounded-xl border border-[var(--stitch-primary)]/30 bg-[var(--stitch-primary-fixed)]/10 px-5 py-4 flex items-start gap-3">
                  <span className="material-symbols-outlined text-[var(--stitch-primary)] text-xl is-filled shrink-0 mt-0.5">task_alt</span>
                  <div className="flex flex-col gap-1">
                    <p className="stitch-font-label text-[13px] font-semibold text-[var(--stitch-primary)] uppercase tracking-wider">
                      Activity &amp; reflection logged automatically
                    </p>
                    <p className="stitch-font-body text-[13px] text-[var(--stitch-on-surface-variant)]">
                      {result.autoActivity.reflectionCreated
                        ? 'A CPD activity and AI-generated reflection have been saved to your record. Review and edit them in Activities.'
                        : 'A CPD activity has been saved to your record. Head to Activities to add a reflection.'}
                    </p>
                    <Link
                      href="/activities"
                      className="stitch-font-label text-[12px] text-[var(--stitch-primary)] hover:underline uppercase tracking-wider mt-1 self-start"
                    >
                      View in Activities →
                    </Link>
                  </div>
                </div>
              )}

              {!result.passed && (
                <button
                  type="button"
                  onClick={() => { setResult(null); setAnswers({}); }}
                  className="px-8 py-4 bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] rounded-full stitch-font-label text-[13px] uppercase tracking-widest font-bold hover:bg-[var(--stitch-primary-container)] transition-colors"
                >
                  Try again
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ModulePage(): React.ReactElement {
  const [, params] = useRoute<{ id: string }>('/modules/:id');
  const id = params?.id ?? '';
  return (
    <AppLayout currentRoute="/learn">
      {id ? <ModuleContent id={id} /> : <p>Module not found.</p>}
    </AppLayout>
  );
}
